import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import { getToken } from '../services/auth';

/* ─────────── Collapsible user form section ─────────── */
function Section({ title, expanded, onToggle, children }) {
    return (
        <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: '12px' }}>
            <button
                onClick={onToggle}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
            >
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--navy)' }}>{title}</div>
                <span style={{ fontSize: '14px', color: 'var(--text-secondary)', transition: 'transform .2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}>▶</span>
            </button>
            {expanded && <div style={{ paddingTop: '8px' }}>{children}</div>}
        </div>
    );
}

/* ─────────── Inline Confirm Overlay ─────────── */
function InlineConfirm({ message, onConfirm, onCancel }) {
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
            <div style={{
                background: '#fff', borderRadius: '10px', padding: '20px', maxWidth: '280px', width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}>
                <div style={{ fontSize: '13px', color: 'var(--navy)', marginBottom: '16px', lineHeight: '1.4' }}>{message}</div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)' }}>Cancel</button>
                    <button onClick={onConfirm} style={{ background: 'var(--orange)', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', color: '#fff', fontWeight: 'bold' }}>Delete</button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────── Toast Alert ─────────────────── */
function Toast({ message, type, onDone }) {
    useEffect(() => {
        const t = setTimeout(onDone, 2400);
        return () => clearTimeout(t);
    }, [onDone]);

    return (
        <div style={{
            position: 'fixed', bottom: '72px', left: '50%', transform: 'translateX(-50%)',
            background: type === 'error' ? '#c0392b' : 'var(--navy)', color: '#fff',
            padding: '8px 16px', borderRadius: '8px', fontSize: '12px', zIndex: 9999,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)', whiteSpace: 'nowrap',
        }}>
            {message}
        </div>
    );
}

export default function AdminPane({ onClose }) {
    const { userEmail } = useAddin();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    
    // Form state
    const [formEmail, setFormEmail] = useState('');
    const [formPassword, setFormPassword] = useState('');
    const [formIsAdmin, setFormIsAdmin] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    // Toast
    const [toast, setToast] = useState(null);
    const showToast = (message, type = 'info') => setToast({ message, type });

    // Inline confirm
    const [confirmState, setConfirmState] = useState(null);
    const requestConfirm = (message) => new Promise((resolve) => {
        setConfirmState({ message, resolve });
    });
    const handleConfirmYes = () => { confirmState?.resolve(true); setConfirmState(null); };
    const handleConfirmNo = () => { confirmState?.resolve(false); setConfirmState(null); };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = getToken();
            const response = await fetch('/api/admin/users', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                throw new Error(`Failed to load users (${response.status})`);
            }
            const data = await response.json();
            setUsers(data.users || []);
        } catch (err) {
            console.error('Error loading users:', err);
            showToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const resetForm = () => {
        setFormEmail('');
        setFormPassword('');
        setFormIsAdmin(false);
        setIsEditMode(false);
        setShowForm(false);
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        if (!formEmail.trim()) {
            showToast('Email is required', 'error');
            return;
        }

        if (!isEditMode && !formPassword) {
            showToast('Password is required for new users', 'error');
            return;
        }

        try {
            const token = getToken();
            const response = await fetch('/api/admin/users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: formEmail.trim(),
                    password: formPassword || undefined,
                    is_admin: formIsAdmin
                })
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || `Save failed (${response.status})`);
            }

            showToast(isEditMode ? 'User updated successfully' : 'User created successfully');
            resetForm();
            fetchUsers();
        } catch (err) {
            console.error('Error saving user:', err);
            showToast(err.message, 'error');
        }
    };

    const handleEditUser = (user) => {
        setFormEmail(user.email);
        setFormPassword('');
        setFormIsAdmin(user.is_admin);
        setIsEditMode(true);
        setShowForm(true);
    };

    const handleDeleteUser = async (emailToDelete) => {
        const confirmed = await requestConfirm(`Are you sure you want to delete user ${emailToDelete}?`);
        if (!confirmed) return;

        try {
            const token = getToken();
            const response = await fetch(`/api/admin/users/${encodeURIComponent(emailToDelete)}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || `Delete failed (${response.status})`);
            }

            showToast('User deleted successfully');
            fetchUsers();
        } catch (err) {
            console.error('Error deleting user:', err);
            showToast(err.message, 'error');
        }
    };

    return (
        <div className="addin-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, color: 'var(--navy)' }}>User Management</h2>
                <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}>&times;</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                
                {/* ── User creation/edit form ── */}
                <Section 
                    title={isEditMode ? "Edit User Details" : "+ Create New User"} 
                    expanded={showForm} 
                    onToggle={() => setShowForm(!showForm)}
                >
                    <form onSubmit={handleSaveUser} style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginBottom: '12px', border: '1px solid var(--border)' }}>
                        <div>
                            <label style={labelStyle}>Email Address</label>
                            <input 
                                type="email" 
                                className="form-input" 
                                placeholder="user@domain.com"
                                value={formEmail} 
                                onChange={e => setFormEmail(e.target.value)}
                                disabled={isEditMode}
                                required
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>{isEditMode ? "Password (Leave blank to keep current)" : "Password"}</label>
                            <input 
                                type="password" 
                                className="form-input" 
                                placeholder={isEditMode ? "••••••••" : "Enter password"}
                                value={formPassword} 
                                onChange={e => setFormPassword(e.target.value)}
                                required={!isEditMode}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '4px 0' }}>
                            <input 
                                type="checkbox" 
                                id="is_admin" 
                                checked={formIsAdmin} 
                                onChange={e => setFormIsAdmin(e.target.checked)}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <label htmlFor="is_admin" style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--navy)', cursor: 'pointer' }}>Administrator Role</label>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                            <button type="button" onClick={resetForm} style={linkBtnStyle}>Cancel</button>
                            <button type="submit" className="confirm-btn teal" style={{ padding: '6px 14px', margin: 0, width: 'auto', fontSize: '12px' }}>
                                {isEditMode ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </form>
                </Section>

                {/* ── User List ── */}
                <div style={{ marginTop: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-secondary)', marginBottom: '8px' }}>Registered Users</div>
                    
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Loading users…</div>
                    ) : users.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', background: '#f9f9f9', borderRadius: '8px' }}>No users found.</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {users.map(user => (
                                <div key={user.id} style={cardStyle}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--navy)', wordBreak: 'break-all' }}>{user.email}</span>
                                                {user.is_admin && <span style={adminBadgeStyle}>Admin</span>}
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                Created: {new Date(user.created_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                        
                                        <div style={{ display: 'flex', gap: '8px', marginLeft: '8px' }}>
                                            <button 
                                                onClick={() => handleEditUser(user)}
                                                style={actionLinkStyle('var(--teal)')}
                                            >
                                                Edit
                                            </button>
                                            
                                            {user.email.toLowerCase() !== userEmail.toLowerCase() && (
                                                <button 
                                                    onClick={() => handleDeleteUser(user.email)}
                                                    style={actionLinkStyle('var(--orange)')}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Overlays */}
            {confirmState && <InlineConfirm message={confirmState.message} onConfirm={handleConfirmYes} onCancel={handleConfirmNo} />}
            {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
        </div>
    );
}

/* ── Inline Styles ── */
const labelStyle = { fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px', display: 'block' };
const linkBtnStyle = { background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' };
const cardStyle = { background: '#fff', borderRadius: '8px', border: '1px solid var(--border)', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const adminBadgeStyle = { fontSize: '9px', background: 'var(--navy)', color: '#fff', borderRadius: '4px', padding: '1px 5px', fontWeight: 'bold' };
const actionLinkStyle = (color) => ({ background: 'none', border: 'none', color: color, cursor: 'pointer', fontSize: '11px', padding: 0 });
